import { useCallback, useEffect, useRef, useState } from 'react'
import { Pagination } from './Pagination'
import { LoadingBar } from './LoadingBar'
import { fetchEnrichmentRunDetail } from '@/api/admin'
import { isEnrichmentRunActive } from '@/types/reading'
import {
  ENRICHMENT_OUTCOMES,
  ENRICHMENT_OUTCOME_COUNTS,
  ENRICHMENT_OUTCOME_LABELS,
  type AdminEnrichmentRunDetail,
  type EnrichmentOutcome,
  type EnrichmentRunDetailResponse,
} from '@/types/admin'
import { focusRing, segmentedRest, segmentedSelected } from '@/components/ui/buttonStyles'

const POLL_INTERVAL_MS = 3000

interface EnrichmentRunDetailProps {
  runId: number
  onBack: () => void
}

/**
 * One run's evidence: not just how many words were handled, but which.
 *
 * A count alone cannot answer the question it provokes — 30 unresolved words is either
 * a broken language pipeline or thirty genuinely obscure words, and only the list tells
 * the two apart. So the counts are the filters.
 */
export function EnrichmentRunDetail({ runId, onBack }: EnrichmentRunDetailProps) {
  const [response, setResponse] = useState<EnrichmentRunDetailResponse | null>(null)
  const [outcome, setOutcome] = useState<EnrichmentOutcome | null>(null)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const mounted = useRef(true)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const load = useCallback(async () => {
    try {
      const result = await fetchEnrichmentRunDetail(runId, { outcome, page })
      if (!mounted.current) return
      setResponse(result.data)
      setError(null)
    } catch {
      if (mounted.current) setError('Could not read this run.')
    } finally {
      if (mounted.current) setLoading(false)
    }
  }, [runId, outcome, page])

  useEffect(() => {
    void load()
  }, [load])

  const run = response?.data ?? null
  const active = isEnrichmentRunActive(run)

  // A run opened mid-flight keeps filling in; a finished one has nothing left to say.
  useEffect(() => {
    if (!active) return

    const timer = setInterval(() => void load(), POLL_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [active, load])

  const choose = useCallback((next: EnrichmentOutcome | null) => {
    setOutcome((current) => (current === next ? null : next))
    setPage(1)
  }, [])

  return (
    <div className="flex flex-col gap-6">
      <button
        type="button"
        onClick={onBack}
        className="self-start inline-flex items-center min-h-11 text-sm font-light text-muted transition-colors duration-200 hover:text-body focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 rounded"
      >
        ← Back to all runs
      </button>

      {loading && !run && <LoadingBar />}

      {error && (
        <p role="alert" className="font-light text-sm text-muted">
          {error}
        </p>
      )}

      {run && (
        <>
          <header>
            <h3 className="font-serif text-lg text-heading">{run.reading.title}</h3>
            <p className="font-mono text-xs uppercase tracking-widest text-muted mt-1">
              {run.reading.language_name} · {run.started_by} · {summaryLine(run)}
            </p>
            {run.error_message && (
              <p className="font-light text-sm text-muted mt-2">{run.error_message}</p>
            )}
          </header>

          <div className="flex flex-wrap gap-2">
            {ENRICHMENT_OUTCOMES.map((key) => (
              <OutcomeChip
                key={key}
                outcome={key}
                count={run[ENRICHMENT_OUTCOME_COUNTS[key]]}
                selected={outcome === key}
                onSelect={() => choose(key)}
              />
            ))}
          </div>

          <WordList run={run} response={response} />

          <Pagination meta={response?.meta ?? null} onPageChange={setPage} />
        </>
      )}
    </div>
  )
}

interface OutcomeChipProps {
  outcome: EnrichmentOutcome
  count: number
  selected: boolean
  onSelect: () => void
}

function OutcomeChip({ outcome, count, selected, onSelect }: OutcomeChipProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={`inline-flex items-center gap-2 min-h-11 px-4 rounded-full text-xs font-normal transition-colors duration-200 ${focusRing} ${
        selected ? segmentedSelected : segmentedRest
      }`}
    >
      {ENRICHMENT_OUTCOME_LABELS[outcome]}{' '}
      <span className="font-mono">{count}</span>
    </button>
  )
}

interface WordListProps {
  run: AdminEnrichmentRunDetail
  response: EnrichmentRunDetailResponse | null
}

function WordList({ run, response }: WordListProps) {
  const words = response?.words ?? []

  // FR-012. These two empties mean opposite things: one is "this run kept no record",
  // the other is "this group is genuinely empty". Saying "no words" for both would
  // report a missing record as a clean result.
  if (words.length === 0) {
    return (
      <p className="font-light text-sm text-muted">
        {run.words_recorded
          ? 'No words in this group.'
          : 'No word detail was recorded for this run — it finished before runs kept one.'}
      </p>
    )
  }

  return (
    <ul className="flex flex-wrap gap-x-4 gap-y-2">
      {words.map((word) => (
        <li key={word.id} className="font-serif text-sm text-body">
          {word.word}
        </li>
      ))}
    </ul>
  )
}

/**
 * "Stopped" is not "failed" — an admin who halted a run on purpose should not be shown
 * an error, and the work it did reach is kept.
 */
function summaryLine(run: AdminEnrichmentRunDetail): string {
  switch (run.status) {
    case 'queued':
      return 'Waiting to start'
    case 'running':
      return `${run.processed_words} of ${run.total_words} words`
    case 'cancelled':
      return `Stopped at ${run.processed_words} of ${run.total_words} words`
    case 'failed':
      return `Failed at ${run.processed_words} of ${run.total_words} words`
    case 'completed':
      return `${run.total_words} words`
  }
}
